/**
 * AddCodeModal.jsx
 *
 * Two-tab modal for adding a missed ICD-10 code to an encounter.
 *
 * Tab 1 — Add Code and Support:
 *   - Code input (onBlur → auto-populate description via /hcc/codes/lookup/{code})
 *   - EVIDENCE panel: PDF viewer (opens on current P3 page), single evidence item
 *   - SUPPORT panel:  PDF viewer (opens on page 1), multiple support items
 *   Both panels use react-pdf-highlighter-extended for text selection.
 *
 * Tab 2 — Review Addition (placeholder):
 *   - Summary of code + evidence + support
 *   - Code interaction analysis placeholder
 *   - "Add to Encounter" confirm button
 *
 * Prerequisites:
 *   npm install react-pdf-highlighter-extended pdfjs-dist
 *
 *   In your app entry point (once):
 *   import { pdfjs } from 'react-pdf-highlighter-extended';
 *   pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
 *
 *   import 'react-pdf-highlighter-extended/dist/style.css';
 */

import React, {
  useState, useEffect, useRef, useCallback, useMemo,
} from 'react';
import { Modal, Spin, message } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import {
  PdfLoader,
  PdfHighlighter,
  usePdfHighlighterContext,
} from 'react-pdf-highlighter-extended';
import AgentConfigService from '../../services/AgentConfigService';

// ── Design tokens — must stay in sync with HCCReviewPage ─────────────────────
const C = {
  navy:        '#162349',
  navyLight:   'rgba(22,35,73,0.08)',
  green:       '#92A372',
  white:       '#FFFFFF',
  border:      '#E2E8F0',
  bg:          '#F8FAFC',
  text:        '#1E293B',
  textMuted:   '#64748B',
  accepted:    '#15803D',
  acceptedBg:  '#DCFCE7',
  yellow:      '#FEF9C3',
  yellowBorder:'#FDE68A',
  blue:        '#1E40AF',
  blueBg:      '#DBEAFE',
  blueBorder:  '#93C5FD',
  red:         '#DC2626',
};

// Stable empty array — never recreated, prevents PdfHighlighter from re-rendering
const EMPTY_HIGHLIGHTS = [];

// ─────────────────────────────────────────────────────────────────────────────
// SelectionCaptureTip
// Rendered by PdfHighlighter as its selectionTip — invisible to the user but
// reads the current selection from PdfHighlighterContext and stores it in the
// parent's state via onCapture.
// ─────────────────────────────────────────────────────────────────────────────
const SelectionCaptureTip = ({ onCapture, startPage }) => {
  const { getCurrentSelection } = usePdfHighlighterContext();
  const lastTextRef = useRef('');

  useEffect(() => {
    const sel = getCurrentSelection?.();
    const text = sel?.content?.text?.trim();
    if (text && text !== lastTextRef.current) {
      lastTextRef.current = text;
      onCapture({
        text,
        pageNumber:   sel.position?.pageNumber ?? 1,
        absolutePage: (startPage ?? 1) + (sel.position?.pageNumber ?? 1) - 1,
      });
    }
  }); // no dep array — runs every time the tip re-renders (only when selection is active)

  // Renders nothing visible — pure side-effect component
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// EmptyHighlightContainer
// react-pdf-highlighter-extended requires children as a HighlightContainer.
// We don't need to render any saved highlights, so this is a no-op.
// ─────────────────────────────────────────────────────────────────────────────
const EmptyHighlightContainer = () => null;

// ─────────────────────────────────────────────────────────────────────────────
// PdfViewerInner — React.memo prevents re-render when pendingSelection changes
// Only re-renders when pdfDocumentParams, startPage, or initialPage change.
// ─────────────────────────────────────────────────────────────────────────────
const PdfViewerInner = React.memo(({
  pdfDocumentParams,
  startPage,
  initialPage,
  onSelectionCapture,
}) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!pdfDocumentParams || initialPage <= 1) return;
    const timer = setTimeout(() => {
      const viewer = containerRef.current?.querySelector('.pdfViewer');
      if (!viewer) return;
      const pages = viewer.querySelectorAll('[data-page-number]');
      const target = Array.from(pages).find(
        p => parseInt(p.getAttribute('data-page-number'), 10) === initialPage
      );
      if (target) target.scrollIntoView({ behavior: 'instant', block: 'start' });
    }, 700);
    return () => clearTimeout(timer);
  }, [pdfDocumentParams, initialPage]);

  return (
    <div
      ref={containerRef}
      style={{ flex: 1, overflow: 'auto', position: 'relative', minHeight: 0 }}
    >
      {!pdfDocumentParams ? (
        <div style={{
          height: '100%', display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          flexDirection: 'column', gap: 12,
        }}>
          <Spin size="large" />
          <span style={{ fontSize: 13, color: C.textMuted }}>Loading encounter pages…</span>
        </div>
      ) : (
        <PdfLoader
          document={pdfDocumentParams}
          beforeLoad={() => (
            <div style={{
              height: '100%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 12,
              background: 'white',
            }}>
              <Spin size="large" />
              <span style={{ fontSize: 13, color: '#64748B' }}>Loading PDF…</span>
            </div>
          )}
        >
          {(pdfDocument) => (
            <PdfHighlighter
              pdfDocument={pdfDocument}
              highlights={EMPTY_HIGHLIGHTS}
              enableAreaSelection={() => false}
              utilsRef={(_utils) => {}}
              selectionTip={
                <SelectionCaptureTip
                  onCapture={onSelectionCapture}
                  startPage={startPage}
                />
              }
            >
              <EmptyHighlightContainer />
            </PdfHighlighter>
          )}
        </PdfLoader>
      )}
    </div>
  );
});

// ─────────────────────────────────────────────────────────────────────────────
// PdfPanel
// One PDF viewer instance. Used twice in the modal — left for Evidence,
// right for Support. Both receive the same blob URL but scroll independently.
// ─────────────────────────────────────────────────────────────────────────────
const PdfPanel = ({
  label,
  pdfDocumentParams,
  startPage,
  initialPage,
  pendingSelection,
  savedItems,
  isSingleItem,
  onSelectionCapture,
  onAddItem,
  onRemoveItem,
}) => {
  const alreadyHasItem = isSingleItem ? !!savedItems : savedItems?.length > 0;
  const canAdd = !!pendingSelection && !(isSingleItem && alreadyHasItem);

  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column',
      border: `1px solid ${C.border}`, borderRadius: 6,
      overflow: 'hidden', minWidth: 0,
    }}>

      {/* Panel header */}
      <div style={{
        padding: '8px 14px', background: C.navy,
        color: 'white', fontSize: 14, fontWeight: 700,
        letterSpacing: '0.08em', flexShrink: 0,
      }}>
        {label}
      </div>

      {/* PDF viewer — memoized, won't remount on selection state changes */}
      <PdfViewerInner
        pdfDocumentParams={pdfDocumentParams}
        startPage={startPage}
        initialPage={initialPage}
        onSelectionCapture={onSelectionCapture}
      />

      {/* Selection preview + action area */}
      <div style={{
        padding: '8px 10px', borderTop: `1px solid ${C.border}`,
        background: C.bg, flexShrink: 0,
      }}>

        {/* Pending selection preview */}
        {pendingSelection ? (
          <div style={{
            fontSize: 13, marginBottom: 7,
            background: C.yellow, border: `1px solid ${C.yellowBorder}`,
            borderRadius: 4, padding: '4px 10px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            <span style={{ fontWeight: 700, color: '#92400E' }}>Selected: </span>
            <span style={{ color: '#78350F' }}>
              {pendingSelection.text.length > 70
                ? pendingSelection.text.slice(0, 70) + '…'
                : pendingSelection.text}
            </span>
            <span style={{ color: '#92400E', marginLeft: 8, fontSize: 11, fontWeight: 600 }}>
              p.{pendingSelection.absolutePage}
            </span>
          </div>
        ) : (
          <div style={{
            fontSize: 12, color: C.textMuted, marginBottom: 7,
            fontStyle: 'italic', textAlign: 'center',
          }}>
            Select text in the PDF above
          </div>
        )}

        {/* Add button */}
        <button
          onClick={() => canAdd && onAddItem(pendingSelection)}
          disabled={!canAdd}
          data-telemetry-label={`add_code_modal_add_${label.toLowerCase()}`}
          style={{
            width: '100%', padding: '7px 0',
            background: canAdd ? C.navy : '#E5E7EB',
            color: canAdd ? 'white' : C.textMuted,
            border: 'none', borderRadius: 4,
            fontSize: 13, fontWeight: 700,
            cursor: canAdd ? 'pointer' : 'not-allowed',
            letterSpacing: '0.04em',
            marginBottom: alreadyHasItem ? 8 : 0,
            transition: 'background 0.15s',
          }}
        >
          + Add as {label}
        </button>

        {/* Saved evidence item (single) */}
        {isSingleItem && savedItems && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <div style={{
              flex: 1, fontSize: 13, fontWeight: 500,
              background: C.acceptedBg, border: `1px solid #86EFAC`,
              borderRadius: 4, padding: '4px 10px', color: C.accepted,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              ✓ {savedItems.text.length > 55
                ? savedItems.text.slice(0, 55) + '…'
                : savedItems.text}
              <span style={{ fontSize: 11, marginLeft: 6, opacity: 0.7 }}>
                p.{savedItems.absolutePage}
              </span>
            </div>
            <button
              onClick={() => onRemoveItem(0)}
              data-telemetry-label={`add_code_modal_remove_${label.toLowerCase()}`}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: C.red, padding: '2px 4px', fontSize: 15, lineHeight: 1,
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* Saved support items (multiple) */}
        {!isSingleItem && savedItems?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {savedItems.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{
                  flexShrink: 0, width: 22, height: 22, borderRadius: '50%',
                  background: C.navy, color: 'white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700,
                }}>
                  {i + 1}
                </span>
                <div style={{
                  flex: 1, fontSize: 13, fontWeight: 500,
                  background: C.blueBg, border: `1px solid ${C.blueBorder}`,
                  borderRadius: 4, padding: '4px 10px', color: C.blue,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                  {item.text.length > 55 ? item.text.slice(0, 55) + '…' : item.text}
                  <span style={{ fontSize: 11, marginLeft: 6, opacity: 0.7 }}>
                    p.{item.absolutePage}
                  </span>
                </div>
                <button
                  onClick={() => onRemoveItem(i)}
                  data-telemetry-label={`add_code_modal_remove_${label.toLowerCase()}`}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: C.red, padding: '2px 4px', fontSize: 15, lineHeight: 1,
                  }}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// ReviewTab — Tab 2: summary + placeholder + confirm
// ─────────────────────────────────────────────────────────────────────────────
const ReviewTab = ({
  icdCode, description, evidenceItem, supportItems,
  onBack, onConfirm, confirming,
}) => {
  const canConfirm = !!(icdCode && description);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Code summary card */}
      <div style={{
        border: `1px solid ${C.border}`, borderRadius: 8,
        padding: '16px 18px', background: C.bg,
      }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.textMuted, marginBottom: 10, letterSpacing: '0.08em' }}>
          CODE TO BE ADDED
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 14 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: C.navy }}>
            {icdCode}
          </span>
          <span style={{ fontSize: 15, color: C.text }}>{description}</span>
        </div>

        {/* Evidence */}
        {evidenceItem ? (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 5, letterSpacing: '0.07em' }}>
              EVIDENCE
            </div>
            <div style={{
              fontSize: 14, background: C.yellow,
              border: `1px solid ${C.yellowBorder}`, borderRadius: 5, padding: '6px 10px',
            }}>
              <span style={{ fontSize: 12, color: '#92400E', fontWeight: 600 }}>
                p.{evidenceItem.absolutePage} ·
              </span>{' '}
              <span style={{ color: C.text }}>"{evidenceItem.text}"</span>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 13, color: C.textMuted, marginBottom: 10, fontStyle: 'italic' }}>
            No evidence text selected
          </div>
        )}

        {/* Support */}
        {supportItems.length > 0 && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.textMuted, marginBottom: 5, letterSpacing: '0.07em' }}>
              SUPPORT ({supportItems.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              {supportItems.map((s, i) => (
                <div key={i} style={{
                  fontSize: 14, background: C.blueBg,
                  border: `1px solid ${C.blueBorder}`, borderRadius: 5, padding: '6px 10px',
                }}>
                  <span style={{ fontSize: 12, color: C.blue, fontWeight: 600 }}>
                    p.{s.absolutePage} ·
                  </span>{' '}
                  <span style={{ color: C.text }}>"{s.text}"</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Code interaction analysis — placeholder */}
      <div style={{
        border: `1.5px dashed ${C.border}`, borderRadius: 8,
        padding: '24px 20px', textAlign: 'center', background: C.bg,
      }}>
        <div style={{ fontSize: 22, marginBottom: 8 }}>⏳</div>
        <div style={{ fontSize: 15, fontWeight: 700, color: C.textMuted }}>
          Code interaction analysis
        </div>
        <div style={{ fontSize: 13, color: C.textMuted, marginTop: 6, lineHeight: 1.5 }}>
          Suppression rules · HCC hierarchy · Combination codes
          <br />
          Coming soon
        </div>
      </div>

      {/* Footer buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 4 }}>
        <button
          onClick={onBack}
          data-telemetry-label="add_code_modal_back"
          style={{
            padding: '9px 22px', background: 'white', color: C.navy,
            border: `1px solid ${C.border}`, borderRadius: 6,
            fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          ← Back
        </button>
        <button
          onClick={onConfirm}
          disabled={!canConfirm || confirming}
          data-telemetry-label="confirm_add_code"
          style={{
            padding: '9px 28px', fontSize: 14, fontWeight: 700,
            borderRadius: 6, border: 'none',
            background: canConfirm && !confirming ? C.green : '#E5E7EB',
            color: canConfirm && !confirming ? 'white' : C.textMuted,
            cursor: canConfirm && !confirming ? 'pointer' : 'not-allowed',
            display: 'flex', alignItems: 'center', gap: 8,
            transition: 'background 0.15s',
          }}
        >
          {confirming
            ? <><Spin size="small" /> Adding…</>
            : <><CheckOutlined /> Add to Encounter</>
          }
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// AddCodeModal — main component
// ─────────────────────────────────────────────────────────────────────────────
const HCCAddCodeModal = ({
  visible,
  onClose,
  chartId,
  sessionId,
  encounterInfo,        // { encounter_id, encounter_number, start_page, end_page, encounter_date|dos }
  defaultEvidencePage,  // absolute chart page the coder is currently viewing in P3
  onCodeAdded,          // callback: (newFinding) => void
}) => {

  // ── Tab ────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState('add');

  // ── Code ───────────────────────────────────────────────────────────────────
  const [icdCode,     setIcdCode]     = useState('');
  const [description, setDescription] = useState('');
  const [codeError,   setCodeError]   = useState('');
  const [codeLooking, setCodeLooking] = useState(false);

  // ── Pending selections (captured from PDF panels) ──────────────────────────
  const [pendingEvidence, setPendingEvidence] = useState(null);
  const [pendingSupport,  setPendingSupport]  = useState(null);

  // ── Saved items ────────────────────────────────────────────────────────────
  const [evidenceItem, setEvidenceItem] = useState(null);   // single item | null
  const [supportItems, setSupportItems] = useState([]);     // array of items

  // ── Base URL for direct PDF loading (no blob needed) ──────────────────────
  const [baseUrl, setBaseUrl] = useState('');

  // ── Misc ───────────────────────────────────────────────────────────────────
  const [confirming, setConfirming] = useState(false);

  // Memoized PDF params — stable reference prevents PdfLoader from reloading on state changes
  const pdfDocumentParams = useMemo(() => {
    if (!baseUrl || !encounterInfo?.encounter_id) return null;
    return {
      url: `${baseUrl}/hcc/charts/${chartId}/encounters/${encounterInfo.encounter_id}/pdf`,
      httpHeaders: { Authorization: `Bearer ${sessionId}` },
    };
  }, [baseUrl, chartId, encounterInfo?.encounter_id, sessionId]);

  // Evidence panel opens on the current P3 page (encounter-relative)
  const evidenceInitialPage = encounterInfo
    ? Math.max(1, (defaultEvidencePage || encounterInfo.start_page) - encounterInfo.start_page + 1)
    : 1;

  // ── Fetch base URL once when modal opens ─────────────────────────────────────
  useEffect(() => {
    if (!visible) return;
    setBaseUrl('');
    AgentConfigService.getWorkQueueServiceUrl()
      .then(url => setBaseUrl(url))
      .catch(() => message.error('Could not reach work queue service'));
  }, [visible]);

  // ── Reset all state on close ───────────────────────────────────────────────
  const resetAndClose = useCallback(() => {
    setActiveTab('add');
    setIcdCode(''); setDescription(''); setCodeError('');
    setPendingEvidence(null); setPendingSupport(null);
    setEvidenceItem(null); setSupportItems([]);
    setBaseUrl('');
    onClose();
  }, [onClose]);

  // ── ICD code lookup on blur ────────────────────────────────────────────────
  const handleCodeBlur = async () => {
    const code = icdCode.trim().toUpperCase();
    if (!code) return;
    setCodeLooking(true);
    setCodeError('');
    setDescription('');
    try {
      const base = await AgentConfigService.getWorkQueueServiceUrl();
      const resp = await fetch(`${base}/hcc/codes/lookup/${encodeURIComponent(code)}`, {
        headers: { Authorization: `Bearer ${sessionId}` }, credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        setDescription(data.description);
        setIcdCode(data.code); // normalize to uppercase
      } else {
        setCodeError(`"${code}" not found in HCC code list`);
      }
    } catch {
      setCodeError('Could not verify code — check connection');
    } finally {
      setCodeLooking(false);
    }
  };

  // ── Add / remove items ─────────────────────────────────────────────────────
  const handleAddEvidence = useCallback((sel) => {
    if (!sel) return;
    setEvidenceItem(sel);
    setPendingEvidence(null);
  }, []);

  const handleAddSupport = useCallback((sel) => {
    if (!sel) return;
    setSupportItems(prev => [...prev, sel]);
    setPendingSupport(null);
  }, []);

  const handleRemoveEvidence = useCallback(() => setEvidenceItem(null), []);

  const handleRemoveSupport = useCallback(
    (i) => setSupportItems(prev => prev.filter((_, idx) => idx !== i)),
    []
  );

  // ── Build and emit the new finding on confirm ──────────────────────────────
  const handleConfirm = async () => {
    if (!icdCode || !description) {
      message.warning('Enter a valid ICD-10 code first');
      return;
    }
    setConfirming(true);
    try {
      const id = `coder-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      const newFinding = {
        finding_id:        id,
        icd10_code:        icdCode,
        description,
        source:            'coder',
        section:           'coder_added',
        meat_category:     'Coder Added',
        support_status:    'gospel',
        display_status:    'direct',
        found_in_sections: ['coder_added'],
        merged_from_count: 1,
        evidence: evidenceItem ? [{
          evidence_id:  `ev-${id}`,
          entity_text:  evidenceItem.text,
          section:      'coder_added',
          page:         evidenceItem.absolutePage,
          char_span:    null,
          context:      { before: '', match: evidenceItem.text, after: '' },
          extracted_by: 'coder',
        }] : [],
        corroboration: {
          status: supportItems.length > 0 ? 'supported' : 'gospel',
          supporting_evidence: supportItems.map((s, i) => ({
            evidence_id:   `sup-${id}-${i}`,
            evidence_type: 'coder_added',
            text:          s.text,
            page:          s.absolutePage,
            char_span:     null,
            context:       { before: '', match: s.text, after: '' },
            section:       'coder_added',
          })),
          llm_reasoning: 'Manually added by coder',
          evaluated_at:  new Date().toISOString(),
        },
        rule_modifications: [],
        hcc_mapping: { mapped: false },
      };

      onCodeAdded(newFinding);
      resetAndClose();
    } finally {
      setConfirming(false);
    }
  };

  const canGoToReview = !!(icdCode && description && !codeError);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal
      open={visible}
      onCancel={resetAndClose}
      footer={null}
      width="92vw"
      centered
      title={null}
      closable={false}
      bodyStyle={{ padding: 0, borderRadius: 10, overflow: 'hidden' }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '86vh', overflow: 'hidden' }}>

        {/* ── Custom tab header ── */}
        <div style={{
          display: 'flex', alignItems: 'flex-end',
          background: C.navy, flexShrink: 0,
          paddingLeft: 8, paddingTop: 6,
        }}>
          {[
            { key: 'add',    label: 'Add Code and Support' },
            { key: 'review', label: 'Review Addition' },
          ].map(tab => {
            const isActive = activeTab === tab.key;
            const locked   = tab.key === 'review' && !canGoToReview;
            return (
              <button
                key={tab.key}
                onClick={() => !locked && setActiveTab(tab.key)}
                data-telemetry-label={`add_code_modal_tab:${tab.key}`}
                style={{
                  padding: '10px 24px',
                  fontSize: 14, fontWeight: 700,
                  border: 'none',
                  cursor: locked ? 'not-allowed' : 'pointer',
                  borderRadius: '8px 8px 0 0',
                  marginRight: 3,
                  background: isActive ? 'white' : 'rgba(255,255,255,0.12)',
                  color: isActive
                    ? C.navy
                    : locked ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.75)',
                  transition: 'all 0.15s',
                  position: 'relative',
                  bottom: 0,
                }}
              >
                {tab.label}
              </button>
            );
          })}
          {/* Close button — right-aligned */}
          <button
            onClick={resetAndClose}
            data-telemetry-label="close_add_code_modal"
            style={{
              marginLeft: 'auto', marginBottom: 4, marginRight: 10,
              background: 'none', border: 'none',
              color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 20,
              lineHeight: 1,
            }}
            onMouseEnter={e => e.currentTarget.style.color = 'white'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.6)'}
          >
            ✕
          </button>
        </div>

        {/* ── Tab content ── */}
        <div style={{
          flex: 1, overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
          padding: '14px 16px', gap: 12, background: 'white',
        }}>

          {/* ── Tab 1: Add Code and Support ── */}
          {activeTab === 'add' && (
            <>
              {/* Code input row */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '11px 14px', border: `1px solid ${C.border}`,
                borderRadius: 6, background: C.bg, flexShrink: 0,
              }}>
                <label style={{
                  fontSize: 15, fontWeight: 700, color: C.navy, whiteSpace: 'nowrap',
                }}>
                  Code
                </label>
                <input
                  value={icdCode}
                  onChange={e => {
                    setIcdCode(e.target.value.toUpperCase());
                    setDescription('');
                    setCodeError('');
                  }}
                  onBlur={handleCodeBlur}
                  onKeyDown={e => e.key === 'Enter' && handleCodeBlur()}
                  placeholder="e.g. I48.20"
                  maxLength={10}
                  style={{
                    width: 130, padding: '7px 10px',
                    fontSize: 16, fontFamily: 'monospace', fontWeight: 700,
                    border: `1.5px solid ${codeError ? C.red : C.border}`,
                    borderRadius: 5, outline: 'none', color: C.navy,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                  }}
                />
                {codeLooking && <Spin size="small" />}
                {codeError && (
                  <span style={{ fontSize: 13, color: C.red, fontWeight: 500 }}>
                    {codeError}
                  </span>
                )}
                {description && (
                  <span style={{ fontSize: 15, color: C.text, fontWeight: 500 }}>
                    {description}
                  </span>
                )}
                {!description && !codeError && !codeLooking && (
                  <span style={{ fontSize: 13, color: C.textMuted, fontStyle: 'italic' }}>
                    Description auto-populates on Tab / Enter
                  </span>
                )}
              </div>

              {/* Two PDF panels */}
              <div style={{ flex: 1, display: 'flex', gap: 12, minHeight: 0 }}>
                <PdfPanel
                label="Evidence"
                pdfDocumentParams={pdfDocumentParams}
                startPage={encounterInfo?.start_page ?? 1}
                initialPage={evidenceInitialPage}
                pendingSelection={pendingEvidence}
                savedItems={evidenceItem}
                isSingleItem={true}
                onSelectionCapture={setPendingEvidence}
                onAddItem={handleAddEvidence}
                onRemoveItem={handleRemoveEvidence}
              />
              <PdfPanel
                label="Support"
                pdfDocumentParams={pdfDocumentParams}
                startPage={encounterInfo?.start_page ?? 1}
                initialPage={1}
                pendingSelection={pendingSupport}
                savedItems={supportItems}
                isSingleItem={false}
                onSelectionCapture={setPendingSupport}
                onAddItem={handleAddSupport}
                onRemoveItem={handleRemoveSupport}
              />
              </div>

              {/* Tab 1 footer */}
              <div style={{
                display: 'flex', justifyContent: 'flex-end',
                gap: 10, flexShrink: 0, paddingTop: 2,
              }}>
                <button
                  onClick={resetAndClose}
                  data-telemetry-label="cancel_add_code_modal"
                  style={{
                    padding: '9px 22px', background: 'white', color: C.navy,
                    border: `1px solid ${C.border}`, borderRadius: 6,
                    fontSize: 14, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => canGoToReview && setActiveTab('review')}
                  disabled={!canGoToReview}
                  data-telemetry-label="add_code_modal_to_review"
                  style={{
                    padding: '9px 26px', fontSize: 14, fontWeight: 700,
                    borderRadius: 6, border: 'none',
                    background: canGoToReview ? C.navy : '#E5E7EB',
                    color: canGoToReview ? 'white' : C.textMuted,
                    cursor: canGoToReview ? 'pointer' : 'not-allowed',
                  }}
                >
                  Review →
                </button>
              </div>
            </>
          )}

          {/* ── Tab 2: Review Addition ── */}
          {activeTab === 'review' && (
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
              <ReviewTab
                icdCode={icdCode}
                description={description}
                evidenceItem={evidenceItem}
                supportItems={supportItems}
                onBack={() => setActiveTab('add')}
                onConfirm={handleConfirm}
                confirming={confirming}
              />
            </div>
          )}

        </div>
      </div>
    </Modal>
  );
};

export default HCCAddCodeModal;